import { useMemo, useState, type ReactNode } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Calculator,
  CheckCircle2,
  Clock3,
  RefreshCw,
  Star,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  calculateChange,
  chartSeries,
  convertFromEur,
  formatDate,
  formatMoney,
  marketIsStale,
  metalValue,
  performanceSeries,
} from "./market";
import type {
  CurrencyCode,
  MarketData,
  MetalRate,
  MetalSymbol,
  WeightUnit,
} from "./types";
const labels: Record<MetalSymbol, string> = {
  XAU: "Gold",
  XAG: "Silber",
  XPT: "Platin",
  XPD: "Palladium",
};
export function CurrencySelector({
  value,
  onChange,
}: {
  value: CurrencyCode;
  onChange: (v: CurrencyCode) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-white/40">
      Währung
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as CurrencyCode)}
        className="h-10 border border-white/10 bg-[#07080d] px-3 font-medium text-white outline-none focus:border-[#e01e37]"
      >
        {(["EUR", "USD", "GBP", "CHF"] as CurrencyCode[]).map((code) => (
          <option key={code}>{code}</option>
        ))}
      </select>
    </label>
  );
}
export function MarketStatus({
  data,
  refreshing,
  refresh,
}: {
  data: MarketData;
  refreshing: boolean;
  refresh: () => void;
}) {
  const stale = marketIsStale(data.latest.timestamp),
    age = Math.max(
      0,
      Math.floor((Date.now() - data.latest.timestamp * 1000) / 60000),
    );
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
      <span
        className={`flex items-center gap-1.5 ${stale ? "text-[#E53935]" : "text-[#1FBF75]"}`}
      >
        {stale ? (
          <Clock3 className="h-4 w-4" />
        ) : (
          <CheckCircle2 className="h-4 w-4" />
        )}
        {stale
          ? "Daten möglicherweise veraltet"
          : age < 1
            ? "Gerade aktualisiert"
            : `Aktualisiert vor ${age} Min.`}
      </span>
      <span>
        Nächste Aktualisierung:{" "}
        {new Date(data.latest.timestamp * 1000 + 300000).toLocaleTimeString(
          "de-DE",
          { hour: "2-digit", minute: "2-digit" },
        )}
      </span>
      <button
        onClick={refresh}
        className="flex h-9 items-center gap-2 border border-white/10 bg-[#07080d] px-3 font-medium text-white"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        Aktualisieren
      </button>
    </div>
  );
}
export function MetalOverviewCard({
  rate,
  data,
  currency,
}: {
  rate: MetalRate;
  data: MarketData;
  currency: CurrencyCode;
}) {
  const change = calculateChange(data.history, rate.symbol),
    series = chartSeries(data.history, rate.symbol, data, currency),
    fx =
      currency === "EUR"
        ? 1
        : (data.forex.rates.find((i) => i.symbol === currency)?.rate ?? 0);
  return (
    <article className="border border-white/10 bg-[#07080d] p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-white">{rate.name}</p>
          <p className="mt-1 font-mono text-xs text-white/40">
            {rate.symbol} · Feinunze
          </p>
        </div>
        {change && (
          <span
            className={`flex items-center gap-1 text-xs font-semibold ${change.percentage >= 0 ? "text-[#1FBF75]" : "text-[#E53935]"}`}
          >
            {change.percentage >= 0 ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )}
            {change.percentage.toLocaleString("de-DE", {
              maximumFractionDigits: 2,
            })}
            %
          </span>
        )}
      </div>
      <p className="mt-6 font-display text-2xl font-bold tabular-nums text-white">
        {formatMoney(convertFromEur(rate.ounce, data, currency), currency)}
      </p>
      <div
        className="mt-4 h-14 min-w-0"
        aria-label={`${rate.name} Kursverlauf`}
      >
        {series.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series}>
              <defs>
                <linearGradient
                  id={`spark-${rate.symbol}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0"
                    stopColor={
                      change && change.percentage < 0 ? "#E53935" : "#1FBF75"
                    }
                    stopOpacity=".24"
                  />
                  <stop
                    offset="1"
                    stopColor={
                      change && change.percentage < 0 ? "#E53935" : "#1FBF75"
                    }
                    stopOpacity="0"
                  />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="price"
                stroke={change && change.percentage < 0 ? "#E53935" : "#1FBF75"}
                strokeWidth={1.5}
                fill={`url(#spark-${rate.symbol})`}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : null}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
        <span className="text-white/40">
          {change
            ? `${change.absolute >= 0 ? "+" : ""}${formatMoney(change.absolute * fx, currency)}`
            : "Veränderung n/v"}
        </span>
        <span className="text-right text-white/40">
          {formatDate(data.latest.timestamp)}
        </span>
      </div>
    </article>
  );
}
export function MetalPriceChart({
  data,
  currency,
  symbol,
  onSymbol,
}: {
  data: MarketData;
  currency: CurrencyCode;
  symbol: MetalSymbol;
  onSymbol: (s: MetalSymbol) => void;
}) {
  const series = chartSeries(data.history, symbol, data, currency),
    change = calculateChange(data.history, symbol),
    rate = data.latest.rates.find((i) => i.symbol === symbol);
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-white">
            Preisentwicklung
          </h2>
          <p className="mt-1 text-sm text-white/40">
            Echte historische Kurse · keine Interpolation
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={symbol}
            onChange={(e) => onSymbol(e.target.value as MetalSymbol)}
            className="h-10 border border-white/10 bg-[#07080d] px-3 text-sm"
          >
            {Object.entries(labels).map(([c, l]) => (
              <option key={c} value={c}>
                {l}
              </option>
            ))}
          </select>
          <div className="flex border border-white/10">
            {["1T", "5T", "1M", "1J"].map((period) => (
              <button
                key={period}
                type="button"
                disabled={period !== "5T"}
                title={
                  period === "5T"
                    ? "Verfügbarer API-Zeitraum"
                    : "Im aktuellen API-Tarif nicht verfügbar"
                }
                className={`h-10 px-3 text-xs font-semibold ${period === "5T" ? "bg-[#e01e37] text-white" : "cursor-not-allowed text-white/25"}`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>
      {series.length ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 border-y border-white/10 py-4 sm:grid-cols-3 xl:grid-cols-6">
            <Stat
              label="Aktuell"
              value={
                rate
                  ? formatMoney(
                      convertFromEur(rate.ounce, data, currency),
                      currency,
                    )
                  : "–"
              }
            />
            <Stat
              label="Höchstwert"
              value={
                change
                  ? formatMoney(
                      convertFromEur(change.high, data, currency),
                      currency,
                    )
                  : "–"
              }
            />
            <Stat
              label="Tiefstwert"
              value={
                change
                  ? formatMoney(
                      convertFromEur(change.low, data, currency),
                      currency,
                    )
                  : "–"
              }
            />
            <Stat
              label="Veränderung"
              value={
                change
                  ? `${change.percentage >= 0 ? "+" : ""}${change.percentage.toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`
                  : "–"
              }
            />
            <Stat
              label="Preis je Gramm"
              value={
                rate
                  ? formatMoney(
                      convertFromEur(rate.gram, data, currency),
                      currency,
                    )
                  : "–"
              }
            />
            <Stat label="Datenpunkte" value={`${series.length} Schlusskurse`} />
          </div>
          <div className="mt-6 h-96 min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="axiqorChart" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#e01e37" stopOpacity=".24" />
                    <stop offset="1" stopColor="#e01e37" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  stroke="rgba(255,255,255,.08)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  stroke="#555"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#555"
                  tickLine={false}
                  axisLine={false}
                  width={68}
                  domain={["auto", "auto"]}
                />
                <Tooltip
                  formatter={(v) => [formatMoney(Number(v), currency), "Preis"]}
                  contentStyle={{
                    background: "#0a0b10",
                    border: "1px solid rgba(255,255,255,.12)",
                    borderRadius: 0,
                    color: "#fff",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke="#e01e37"
                  strokeWidth={2.5}
                  fill="url(#axiqorChart)"
                  dot={{ r: 3, fill: "#e01e37" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <EmptyHistory />
      )}
    </section>
  );
}

export function MetalPerformanceComparison({ data }: { data: MarketData }) {
  const series = performanceSeries(data.history);
  const colors: Record<MetalSymbol, string> = {
    XAU: "#d6a94a",
    XAG: "#aab4c3",
    XPT: "#64a7d8",
    XPD: "#e01e37",
  };
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">
            Metalle im Vergleich
          </h2>
          <p className="mt-1 text-sm text-white/40">
            Relative Entwicklung ab Beginn des verfügbaren Zeitraums
          </p>
        </div>
        <span className="font-mono text-xs text-white/30">
          Basis 0 % · 5 Tage
        </span>
      </div>
      {series.length > 1 ? (
        <div className="mt-6 h-72 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid stroke="rgba(255,255,255,.08)" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#555"
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#555"
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${Number(value).toFixed(1)} %`}
                width={58}
              />
              <Tooltip
                formatter={(value, name) => [
                  `${Number(value).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`,
                  labels[name as MetalSymbol],
                ]}
                contentStyle={{
                  background: "#0a0b10",
                  border: "1px solid rgba(255,255,255,.12)",
                  borderRadius: 0,
                  color: "#fff",
                }}
              />
              <Legend formatter={(value) => labels[value as MetalSymbol]} />
              {(Object.keys(labels) as MetalSymbol[]).map((metal) => (
                <Line
                  key={metal}
                  type="monotone"
                  dataKey={metal}
                  stroke={colors[metal]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyHistory />
      )}
    </section>
  );
}

export function MetalValueCalculator({
  data,
  currency,
}: {
  data: MarketData;
  currency: CurrencyCode;
}) {
  const [symbol, setSymbol] = useState<MetalSymbol>("XAU"),
    [weight, setWeight] = useState("10"),
    [unit, setUnit] = useState<WeightUnit>("gram"),
    rate = data.latest.rates.find((i) => i.symbol === symbol)!;
  const value = metalValue(
    rate,
    Number(weight.replace(",", ".")),
    unit,
    data,
    currency,
  );
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center bg-[#e01e37]/10 text-[#e01e37]">
          <Calculator className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold text-white">
            Metallwert-Rechner
          </h2>
          <p className="text-sm text-white/40">
            Gewicht zum aktuellen Kurs bewerten
          </p>
        </div>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Field label="Metall">
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value as MetalSymbol)}
            className="market-input"
          >
            {data.latest.rates.map((i) => (
              <option key={i.symbol} value={i.symbol}>
                {i.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Gewicht">
          <input
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            inputMode="decimal"
            className="market-input"
          />
        </Field>
        <Field label="Einheit">
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value as WeightUnit)}
            className="market-input"
          >
            <option value="gram">Gramm</option>
            <option value="kilogram">Kilogramm</option>
            <option value="ounce">Feinunze</option>
          </select>
        </Field>
      </div>
      <div className="mt-6 border-l-2 border-[#e01e37] bg-white/[.025] p-5">
        <p className="text-xs uppercase tracking-wider text-white/40">
          Aktueller Metallwert
        </p>
        <p className="mt-2 font-display text-3xl font-bold tabular-nums">
          {formatMoney(value, currency)}
        </p>
        <p className="mt-2 text-xs text-white/40">
          Kurs:{" "}
          {formatMoney(convertFromEur(rate.ounce, data, currency), currency)} /
          Feinunze · {formatDate(data.latest.timestamp)}
        </p>
      </div>
    </section>
  );
}
type SortKey = "name" | "symbol" | "ounce" | "gram";
export function MetalMarketTable({
  data,
  currency,
}: {
  data: MarketData;
  currency: CurrencyCode;
}) {
  const [sort, setSort] = useState<SortKey>("name"),
    [desc, setDesc] = useState(false),
    [favorites, setFavorites] = useState<MetalSymbol[]>([]);
  const rows = useMemo(
    () =>
      [...data.latest.rates].sort((a, b) => {
        const av =
            sort === "name" ? a.name : sort === "symbol" ? a.symbol : a[sort],
          bv =
            sort === "name" ? b.name : sort === "symbol" ? b.symbol : b[sort];
        return (
          (typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av).localeCompare(String(bv))) * (desc ? -1 : 1)
        );
      }),
    [data, sort, desc],
  );
  const changeSort = (key: SortKey) => {
    if (sort === key) setDesc((v) => !v);
    else {
      setSort(key);
      setDesc(false);
    }
  };
  return (
    <section className="border border-white/10 bg-[#07080d]">
      <div className="border-b border-white/10 p-5">
        <h2 className="font-display text-xl font-bold">Markttabelle</h2>
        <p className="mt-1 text-sm text-white/40">
          Alle vorhandenen Metallwerte
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1050px] text-left text-sm">
          <thead className="bg-white/[.025] text-xs uppercase tracking-wider text-white/40">
            <tr>
              <th className="p-4">★</th>
              <Header label="Metall" k="name" action={changeSort} />
              <Header label="Symbol" k="symbol" action={changeSort} />
              <th className="p-4">Aktueller Preis</th>
              <th className="p-4">Währung</th>
              <Header label="Feinunze" k="ounce" action={changeSort} />
              <Header label="Gramm" k="gram" action={changeSort} />
              <th className="p-4">Tagesänderung</th>
              <th className="p-4">Tageshoch</th>
              <th className="p-4">Tagestief</th>
              <th className="p-4">Aktualisiert</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.map((rate) => (
              <tr key={rate.symbol} className="hover:bg-white/[.025]">
                <td className="p-4">
                  <button
                    onClick={() =>
                      setFavorites((c) =>
                        c.includes(rate.symbol)
                          ? c.filter((i) => i !== rate.symbol)
                          : [...c, rate.symbol],
                      )
                    }
                  >
                    <Star
                      className={`h-4 w-4 ${favorites.includes(rate.symbol) ? "fill-[#e01e37] text-[#e01e37]" : "text-[#A7AFBA]"}`}
                    />
                  </button>
                </td>
                <td className="p-4 font-semibold">{rate.name}</td>
                <td className="p-4 font-mono">{rate.symbol}</td>
                <td className="p-4 tabular-nums">
                  {formatMoney(
                    convertFromEur(rate.ounce, data, currency),
                    currency,
                  )}
                </td>
                <td className="p-4">{currency}</td>
                <td className="p-4 tabular-nums">
                  {formatMoney(
                    convertFromEur(rate.ounce, data, currency),
                    currency,
                  )}
                </td>
                <td className="p-4 tabular-nums">
                  {formatMoney(
                    convertFromEur(rate.gram, data, currency),
                    currency,
                  )}
                </td>
                <td className="p-4 text-white/40">Nicht verfügbar</td>
                <td className="p-4 text-white/40">Nicht verfügbar</td>
                <td className="p-4 text-white/40">Nicht verfügbar</td>
                <td className="p-4 text-xs text-white/40">
                  {formatDate(data.latest.timestamp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-white/40">{label}</p>
      <p className="mt-1 font-semibold tabular-nums">{value}</p>
    </div>
  );
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label>
      <span className="mb-2 block text-xs font-medium text-white/40">
        {label}
      </span>
      {children}
    </label>
  );
}
function Header({
  label,
  k,
  action,
}: {
  label: string;
  k: SortKey;
  action: (k: SortKey) => void;
}) {
  return (
    <th className="p-4">
      <button onClick={() => action(k)} className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="h-3 w-3" />
      </button>
    </th>
  );
}
function EmptyHistory() {
  return (
    <div className="mt-8 border border-dashed border-white/10 bg-white/[.025] p-10 text-center text-sm text-white/40">
      Keine historischen Daten verfügbar.
    </div>
  );
}
